// React
import React, { useEffect, useMemo, useRef, useState } from 'react';

// Refine
import { useTranslation } from '@refinedev/core';

// Mantine
import {
  Autocomplete,
  AutocompleteProps,
  Group,
  Highlight,
  Loader,
  OptionsFilter,
  Text,
  useComputedColorScheme,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';

// API
import { useSearchTariffs } from 'api/resource';

// Interfaces
import { Tariff } from 'interfaces';

// Sentry
import * as Sentry from '@sentry/react';
import { useCurrentSiteStore } from 'store';

/**
 * Props for the `CustomReportTariffSearch` component.
 * @interface CustomReportTariffSearchProps
 * @property {string} [defaultValue] - The default text for the search input field
 * @property {(tariff: Tariff) => void} onChange - Callback with the selected Tariff object
 */
interface CustomReportTariffSearchProps {
  defaultValue?: string;
  onChange: (tariff: Tariff) => void;
}

/**
 * CustomReportTariffSearch — a standalone tariff autocomplete for reports.
 *
 * Differences from TariffSearch:
 * - Does not rely on ClaimFormProvider/useActiveForm
 * - Removes specialtyCode and serviceStart props
 * - Emits the full Tariff object via `onChange`
 */
export const CustomReportTariffSearch: React.FC<
  CustomReportTariffSearchProps
> = ({ defaultValue = '', onChange }) => {
  const { translate } = useTranslation();
  const [searchValue, setSearchValue] = useState<string>(defaultValue);
  const [debounced] = useDebouncedValue<string>(searchValue.trim(), 500);

  const computedColorScheme = useComputedColorScheme('dark');
  const color = computedColorScheme === 'dark' ? 'white' : 'black';
  const ref = useRef<HTMLInputElement>(null);
  const { province } = useCurrentSiteStore();

  const {
    data: tariffs = [],
    isLoading,
    isFetching,
    refetch,
  } = useSearchTariffs({
    searchValue: debounced,
    province,
  });

  useEffect(() => {
    refetch();
  }, [debounced]);

  const showNoTariffMessage =
    debounced.length > 0 && !isLoading && !isFetching && tariffs.length === 0;

  const options = useMemo(() => {
    if (showNoTariffMessage) {
      const msg = translate('components.tariffSearch.noTariffsFound');
      return [{ value: '__NO_RESULTS__', label: msg, disabled: true }];
    }

    return tariffs.map((t) => ({
      value: JSON.stringify(t),
      label: `${t.prefix}${t.code} - ${t.description}`,
    }));
  }, [showNoTariffMessage, tariffs, translate]);

  const renderAutocompleteOption: AutocompleteProps['renderOption'] = ({
    option,
  }: {
    option: { value: string; label: string } | null;
  }) => {
    if (!option) return <Loader />;

    if (option.value === '__NO_RESULTS__') {
      return (
        <Text size="sm" c="dimmed">
          {option.label}
        </Text>
      );
    }

    let value: Tariff | null = null;
    try {
      value = JSON.parse(option.value);
    } catch {
      return <Text size="sm">{translate('noResults')}</Text>;
    }
    if (!value) return <Text size="sm">{translate('noResults')}</Text>;

    return (
      <Group gap="xs">
        <div>
          <Text size="sm">
            <Highlight highlight={searchValue}>
              {`${value.prefix}${value.code}`}
            </Highlight>
          </Text>
          <Group gap="xs">
            <Text size="xs" opacity={0.5}>
              {translate('description')}
            </Text>
            <Highlight size="xs" highlight={searchValue}>
              {value.description ?? ''}
            </Highlight>
          </Group>
        </div>
      </Group>
    );
  };

  const optionsFilter: OptionsFilter = ({ options }) => options;

  return (
    <Autocomplete
      ref={ref}
      comboboxProps={{ withinPortal: true }}
      defaultValue={defaultValue}
      placeholder={translate('components.tariffSearch.placeholder')}
      style={{ width: '90%' }}
      radius="md"
      size="sm"
      data-testid="custom-report-tariff-search-autocomplete"
      c={color}
      data={options}
      renderOption={renderAutocompleteOption}
      // Select all text, on focus and mouse down for quicker re-searching
      onFocus={() => {
        const el = ref.current;
        if (el) {
          // Defer to ensure Mantine/internal focus completes before selection
          requestAnimationFrame(() => {
            el.select();
          });
        }
      }}
      onMouseDown={(e) => {
        const el = ref.current;
        if (!el) return;
        // Prevent caret placement so click selects all text reliably
        e.preventDefault();
        el.focus();
        // Ensure focus is applied before selection
        setTimeout(() => el.select(), 0);
      }}
      onChange={(value) => setSearchValue(value)}
      onOptionSubmit={(value) => {
        if (value === '__NO_RESULTS__') return;
        try {
          const t: Tariff = JSON.parse(value);
          onChange(t);
        } catch (error) {
          Sentry.captureException(error);
        }
      }}
      filter={optionsFilter}
      rightSection={
        (isLoading || isFetching) && (
          <Loader
            style={{ position: 'absolute', right: 15, top: 10 }}
            size={20}
            color={color}
          />
        )
      }
    />
  );
};
